import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Download, RefreshCw, X, Check, ChevronDown, SlidersHorizontal } from 'lucide-react';
import { apiClient } from '@rti/shared';
import { buildDriversPdf, driversPdfFilename, getAbbr as builderGetAbbr } from '@/utils/driversPdfBuilder';
import { useAuth } from '@/contexts/AuthContext';

interface DriversExportButtonProps {
  selectedDate: string | null;
  driver: string;
  driver_report_content: string;
  filteredReports: any[];
  visibleReports?: any[];
  trends?: any[];
  disabled?: boolean;
  lockedForTrial?: boolean;
}

const DriversExportButton: React.FC<DriversExportButtonProps> = ({
  selectedDate,
  driver,
  driver_report_content,
  filteredReports,
  visibleReports,
  trends = [],
  disabled = false,
  lockedForTrial = false
}) => {
  const { user } = useAuth();
  const [isGenerating, setIsGenerating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showDropdown) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showDropdown]);
  const reportsSource = useMemo(() => (visibleReports && visibleReports.length ? visibleReports : filteredReports), [visibleReports, filteredReports]);

  const getAbbr = builderGetAbbr;

  const [selectedResins, setSelectedResins] = useState<Set<string>>(
    new Set(reportsSource.map(getAbbr))
  );

  const resinOptions = useMemo(() => {
    return reportsSource.map(report => ({
      id: getAbbr(report),
      label: getAbbr(report),
      fullName: report.driverReport || 'Market Report'
    }));
  }, [reportsSource]);

  const handleSelectAll = () => {
    setSelectedResins(new Set(resinOptions.map(r => r.id)));
  };

  const handleDeselectAll = () => {
    setSelectedResins(new Set());
  };

  const handleToggleResin = (resinId: string) => {
    const newSelected = new Set(selectedResins);
    if (newSelected.has(resinId)) {
      newSelected.delete(resinId);
    } else {
      newSelected.add(resinId);
    }
    setSelectedResins(newSelected);
  };

  const generatePDFWithResins = async (resinsToExport: Set<string>, isExportAll: boolean = false) => {
    if (lockedForTrial) return;
    if (!selectedDate || resinsToExport.size === 0) {
      alert('Please select at least one driver to export');
      return;
    }

    const pdfWindow = window.open('', '_blank');
    const openInWindow = (url: string) => {
      if (pdfWindow) {
        pdfWindow.location.href = url;
      } else {
        window.open(url, '_blank');
      }
    };

    setIsGenerating(true);
    try {
      const fileName = driversPdfFilename(selectedDate);

      if (isExportAll) {
        try {
          const checkData = await apiClient.checkCachedPdfExists(fileName);
          if (checkData.exists) {
            const pdfBlob = await apiClient.downloadCachedPdf(fileName);
            const pdfUrl = URL.createObjectURL(pdfBlob);
            openInWindow(pdfUrl);
            setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
            setShowModal(false);
            return;
          }
        } catch (error) {
          console.log('No existing PDF found, generating new one');
        }
      }

      const selectedReports = reportsSource.filter(r => resinsToExport.has(getAbbr(r)));
      const pdfBlob = await buildDriversPdf({ reports: selectedReports, selectedDate, trends, userEmail: user?.email || '' });

      if (isExportAll) {
        try {
          const saveSuccess = await apiClient.saveDriverPdf(pdfBlob, fileName, selectedDate);
          if (saveSuccess) {
            const savedBlob = await apiClient.downloadCachedPdf(fileName);
            const pdfUrl = URL.createObjectURL(savedBlob);
            openInWindow(pdfUrl);
            setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
          } else {
            const pdfUrl = URL.createObjectURL(pdfBlob);
            openInWindow(pdfUrl);
            setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
          }
        } catch (error) {
          console.error('Failed to save PDF to server:', error);
          const pdfUrl = URL.createObjectURL(pdfBlob);
          openInWindow(pdfUrl);
          setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
        }
      } else {
        const pdfUrl = URL.createObjectURL(pdfBlob);
        openInWindow(pdfUrl);
        setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
      }

      setShowModal(false);
    } catch (error) {
      console.error('Error generating PDF:', error);
      pdfWindow?.close();
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleExportAll = async () => {
    const allResins = new Set(resinOptions.map(r => r.id));
    await generatePDFWithResins(allResins, true);
  };

  const handleExportSelected = async () => {
    await generatePDFWithResins(selectedResins, false);
  };

  const isDisabled = lockedForTrial || disabled || isGenerating || !selectedDate || !filteredReports.length;

  return (
    <>
    <div className="relative group/export-lock" ref={dropdownRef}>
      {lockedForTrial && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-rs-jet border border-rs-off-black text-rs-off-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover/export-lock:opacity-100 transition-opacity pointer-events-none z-50 shadow-lg">
          Exports allowed on paid subscriptions only
        </div>
      )}
      <div className={`flex items-center rounded-md overflow-hidden ${isDisabled ? 'opacity-50' : ''}`}>
        <div className="w-px self-stretch bg-rs-off-black/40" />
        <button
          onClick={() => setShowDropdown(prev => !prev)}
          disabled={isDisabled}
          className="px-2 py-2 bg-rs-green !text-black font-medium transition-all hover:bg-rs-green/80 disabled:cursor-not-allowed"
          title="More export options"
          aria-label="More export options"
        >
           <Download className="w-4 h-4" /> <ChevronDown className={`w-4 h-4 transition-transform duration-150 ${showDropdown ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {showDropdown && (
        <div className="absolute right-0 top-full mt-1 bg-rs-jet border border-rs-off-black rounded-md shadow-xl z-10 min-w-[200px] overflow-hidden">
          <button
            onClick={() => { handleExportAll(); setShowDropdown(false); }}
            disabled={isDisabled}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-rs-off-white hover:bg-rs-jet-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4 text-rs-green" />
            Export All Drivers
          </button>
          <div className="border-t border-rs-off-black" />
          <button
            onClick={() => { setShowModal(true); setShowDropdown(false); }}
            disabled={isDisabled}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-rs-off-white hover:bg-rs-jet-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <SlidersHorizontal className="w-4 h-4 text-rs-green" />
            Select Drivers...
          </button>
        </div>
      )}
    </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-rs-off-black rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-auto border border-rs-green border-opacity-30">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-rs-off-white">Select Drivers to Download</h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-rs-light-gray hover:text-rs-off-white transition-colors"
                  title="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex gap-2 mb-4">
                <button
                  onClick={handleSelectAll}
                  className="flex-1 px-3 py-2 bg-rs-jet hover:bg-rs-jet-light text-rs-off-white rounded-md transition-colors text-sm"
                >
                  Select All
                </button>
                <button
                  onClick={handleDeselectAll}
                  className="flex-1 px-3 py-2 bg-rs-jet hover:bg-rs-jet-light text-rs-off-white rounded-md transition-colors text-sm"
                >
                  Deselect All
                </button>
              </div>

              <div className="flex flex-col gap-2 mb-6 max-h-96 overflow-y-auto">
                {resinOptions.map((resin) => (
                  <label
                    key={resin.id}
                    className="flex items-center gap-3 p-3 bg-rs-jet hover:bg-rs-jet-light rounded-md cursor-pointer transition-colors"
                  >
                    <div className="relative flex items-center justify-center">
                      <input
                        type="checkbox"
                        checked={selectedResins.has(resin.id)}
                        onChange={() => handleToggleResin(resin.id)}
                        className="sr-only"
                      />
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          selectedResins.has(resin.id)
                            ? 'bg-rs-green border-rs-green'
                            : 'border-rs-light-gray'
                        }`}
                      >
                        {selectedResins.has(resin.id) && (
                          <Check className="w-4 h-4 text-black" />
                        )}
                      </div>
                    </div>
                    <span className="text-rs-off-white flex-1">{resin.label}</span>
                  </label>
                ))}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 bg-rs-jet hover:bg-rs-jet-light text-rs-off-white rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExportSelected}
                  disabled={selectedResins.size === 0 || isGenerating}
                  className={`flex-1 px-4 py-2 rounded-md transition-colors ${
                    selectedResins.size === 0 || isGenerating
                      ? 'bg-rs-light-gray opacity-50 cursor-not-allowed !text-black'
                      : 'bg-rs-green hover:bg-rs-green/80 !text-black'
                  }`}
                >
                  {isGenerating ? (
                    <>
                      <RefreshCw className="w-4 h-4 inline mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    `Export ${selectedResins.size} Driver${selectedResins.size !== 1 ? 's' : ''}`
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DriversExportButton;
